import * as manufacturingApi from '../../lib/api/manufacturing';

/**
 * 라우팅 공정 상태 동기화 유틸
 * 
 * 각 탭(설계/원자재/생산/완료)에서 데이터 변경 시
 * 해당 워크플로우 라우팅 공정의 상태를 자동 업데이트합니다.
 *
 * 워크플로우 공정 매핑:
 *  - 설계 탭 → processNm: '설계' (processSeq: 1)
 *  - 생산 탭 → 생산 관련 공정들 (processSeq: 2~6, 몰드/코어/가공 등)
 *  - 완료 탭 → processNm: '생산완료' (마지막 공정)
 */

// 상태 진행 순서
const STATUS_ORDER = { 'PENDING': 0, 'IN_PROGRESS': 1, 'COMPLETED': 2, 'SKIPPED': 2 };

/**
 * 라우팅 데이터를 조회하여 특정 공정을 찾고 상태를 업데이트
 * 
 * @param {string} planId - 생산계획 UUID
 * @param {Function} processFinder - routing.details에서 대상 공정을 찾는 함수
 * @param {string} newStatus - 변경할 상태 ('IN_PROGRESS' | 'COMPLETED')
 * @param {Function} onSuccess - 성공 시 콜백 (선택)
 * @returns {boolean} 성공 여부
 */
export const updateRoutingProcess = async (planId, processFinder, newStatus, onSuccess) => {
  if (!planId) return false;

  try {
    // 1. 라우팅 데이터 조회
    const routingResponse = await manufacturingApi.getRoutingsByPlan(planId);
    if (!routingResponse.data.success || !routingResponse.data.data?.length) return false;

    const routing = routingResponse.data.data[0];
    if (!routing.details || routing.details.length === 0) return false;

    // 2. 대상 공정 찾기
    const targetProcess = processFinder(routing.details);
    if (!targetProcess) return false;

    // 3. 이미 같은 상태이거나 더 진행된 상태라면 스킵
    const currentOrder = STATUS_ORDER[targetProcess.processStatus] || 0;
    const newOrder = STATUS_ORDER[newStatus] || 0;
    if (currentOrder >= newOrder) return false;

    // 4. 상태 변경 API 호출
    await manufacturingApi.updateRoutingProcessStatus(targetProcess.routingDetUuid, {
      processStatus: newStatus,
    });
    console.log(`라우팅 공정 상태 변경: "${targetProcess.processNm}" ${targetProcess.processStatus} → ${newStatus}`);

    // 5. 성공 콜백
    if (onSuccess) onSuccess();
    return true;
  } catch (err) {
    console.error('라우팅 공정 상태 업데이트 실패:', err);
    return false;
  }
};

// ============================================
// 탭별 편의 함수
// ============================================

/**
 * 설계 탭 → 라우팅 "설계" 공정 업데이트
 */
export const syncDesignProcess = (planId, newStatus, onSuccess) => {
  return updateRoutingProcess(
    planId,
    (details) => details.find(d => d.processNm === '설계' || d.processSeq === 1),
    newStatus,
    onSuccess
  );
};

/**
 * 생산 탭 → 라우팅 생산 관련 공정들 일괄 업데이트
 * 생산 시작 시: 설계 다음 공정(seq 2)부터 마지막 전 공정까지 IN_PROGRESS
 * 생산 완료 시: 설계 다음 공정부터 마지막 전 공정까지 COMPLETED
 */
export const syncProductionProcesses = async (planId, newStatus, onSuccess) => {
  if (!planId) return false;

  try {
    const routingResponse = await manufacturingApi.getRoutingsByPlan(planId);
    if (!routingResponse.data.success || !routingResponse.data.data?.length) return false;

    const routing = routingResponse.data.data[0];
    if (!routing.details || routing.details.length === 0) return false;

    const maxSeq = Math.max(...routing.details.map(d => d.processSeq));

    // 설계(seq 1)와 생산완료(마지막 seq) 제외한 중간 공정들
    const productionProcesses = routing.details.filter(
      d => d.processSeq > 1 && d.processSeq < maxSeq
    );

    let updated = false;
    for (const proc of productionProcesses) {
      const currentOrder = STATUS_ORDER[proc.processStatus] || 0;
      const newOrder = STATUS_ORDER[newStatus] || 0;
      if (currentOrder >= newOrder) continue;

      try {
        await manufacturingApi.updateRoutingProcessStatus(proc.routingDetUuid, {
          processStatus: newStatus,
        });
        console.log(`라우팅 공정 상태 변경: "${proc.processNm}" ${proc.processStatus} → ${newStatus}`);
        updated = true;
      } catch (err) {
        console.error(`공정 "${proc.processNm}" 상태 변경 실패:`, err);
      }
    }

    if (updated && onSuccess) onSuccess();
    return updated;
  } catch (err) {
    console.error('생산 라우팅 동기화 실패:', err);
    return false;
  }
};

/**
 * 완료 탭 → 라우팅 "생산완료" 공정 업데이트 (마지막 공정)
 * "마지막 공정"은 processSeq 값이 가장 큰 공정을 의미(QC 전용 공정 아님, 순수 시퀀스 최대값 기준)
 */
export const syncCompletionProcess = (planId, newStatus, onSuccess) => {
  return updateRoutingProcess(
    planId,
    (details) => {
      const maxSeq = Math.max(...details.map(d => d.processSeq));
      return details.find(d => d.processSeq === maxSeq);
    },
    newStatus,
    onSuccess
  );
};

/**
 * 라우팅 공정의 일자 필드(planStartDt/planEndDt/actualStartDt/actualEndDt 등)를 날짜만
 * 표시할 때 쓰는 방어적 절삭 헬퍼.
 *
 * 백엔드가 DATE_FORMAT(..., '%Y-%m-%d %H:%i:%s') 로 내려주는 datetime 문자열에서
 * 앞 10자리(yyyy-MM-dd)만 잘라낸다. 값이 없거나 형식이 예상과 다르면(파싱 시도하지 않고)
 * 빈 문자열로 방어한다 - 목록/컬럼처럼 "날짜만" 필요한 자리에 쓰고, 시각까지 보여줘야 하는
 * 상세 화면의 읽기전용 필드에는 쓰지 않는다(원본 문자열을 그대로 보여줘야 한다).
 */
export const toDateOnly = (value) => {
  if (!value) return '';
  const str = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(str) ? str.slice(0, 10) : '';
};
